import React, { useState, useEffect } from 'react'
import { apiClient } from '@/utils/api'
import { isThisWeek, differenceInDays, format } from 'date-fns'
import {
  parseDate, parseLocalDate, formatEventDate, formatTime, getDaysFromToday, isUUID
} from '@/utils/dateUtils'
import { 
  Calendar, MapPin, Users, Clock, AlertCircle, 
  GraduationCap, Bus, Camera, Trophy, Book
} from 'lucide-react'

interface SchoolEvent {
  id: string
  school_id: string
  school_name: string
  event_name: string
  event_date: string
  event_time?: string
  event_type: string
  description?: string
  location?: string
  all_day: boolean
  requires_rsvp: boolean
  is_mandatory: boolean
  affected_students?: string[]
}

interface SchoolEventsProps {
  compact?: boolean
  daysAhead?: number
  showPastEvents?: boolean
  userId?: string  // Optional: filter events by user's school
}

export const SchoolEvents: React.FC<SchoolEventsProps> = ({
  compact = false,
  daysAhead = 30,
  showPastEvents = false,
  userId
}) => {
  const [events, setEvents] = useState<SchoolEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'thisweek' | 'thismonth'>(compact ? 'all' : 'thisweek')

  useEffect(() => {
    loadEvents()
  }, [userId])

  const loadEvents = async () => {
    try {
      setLoading(true)
      const params: any = { days_ahead: daysAhead }
      if (userId) {
        params.user_id = userId
      }
      const response = await apiClient.get('/api/schools/events', { params })
      
      // Map API response to component format
      const rawEvents = response.data || response || []
      const mappedEvents = rawEvents.map((e: any) => ({
        id: e.id || Math.random().toString(),
        school_id: '',
        school_name: e.school || '',
        event_name: e.name || '',
        event_date: e.date || '',
        event_time: e.early_release || '',
        event_type: e.type || 'other',
        description: e.description || '',
        location: '',
        all_day: !e.early_release,
        requires_rsvp: false,
        is_mandatory: false,
        affected_students: []
      }))
      
      // Sort events by date (using parseLocalDate to avoid timezone issues)
      const sortedEvents = mappedEvents
        .filter((e: SchoolEvent) => e && e.event_date) // Filter out events without dates
        .sort((a: SchoolEvent, b: SchoolEvent) =>
          parseLocalDate(a.event_date).getTime() - parseLocalDate(b.event_date).getTime()
        )

      // Filter out past events unless specified
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const filteredEvents = showPastEvents
        ? sortedEvents
        : sortedEvents.filter((e: SchoolEvent) => {
            const eventDate = parseLocalDate(e.event_date);
            return eventDate >= today;
          })
      
      setEvents(filteredEvents)
    } catch (error) {
      console.error('Failed to load school events:', error)
      setEvents([])
    } finally {
      setLoading(false)
    }
  }

  const getEventIcon = (eventType: string) => {
    const icons: Record<string, JSX.Element> = {
      'holiday': <Calendar className="w-5 h-5" />,
      'break': <Calendar className="w-5 h-5" />,
      'picture_day': <Camera className="w-5 h-5" />,
      'field_trip': <Bus className="w-5 h-5" />,
      'performance': <Trophy className="w-5 h-5" />,
      'meeting': <Users className="w-5 h-5" />,
      'testing': <Book className="w-5 h-5" />,
      'sports': <Trophy className="w-5 h-5" />,
      'other': <GraduationCap className="w-5 h-5" />
    }
    return icons[eventType] || icons.other
  }

  const getEventColor = (eventType: string) => {
    const colors: Record<string, string> = {
      'holiday': 'bg-green-100 text-green-800 border-green-200',
      'break': 'bg-blue-100 text-blue-800 border-blue-200',
      'picture_day': 'bg-purple-100 text-purple-800 border-purple-200',
      'field_trip': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'performance': 'bg-pink-100 text-pink-800 border-pink-200',
      'meeting': 'bg-orange-100 text-orange-800 border-orange-200',
      'testing': 'bg-red-100 text-red-800 border-red-200',
      'sports': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'other': 'bg-gray-100 text-gray-800 border-gray-200'
    }
    return colors[eventType] || colors.other
  }

  // formatEventDate is now imported from dateUtils

  const formatEventTimeDisplay = (event: SchoolEvent) => {
    if (event.all_day) return 'All Day'
    return formatTime(event.event_time)
  }

  const formatEventTime = formatEventTimeDisplay // Alias for compatibility

  const filteredEvents = events.filter(event => {
    const eventDate = parseLocalDate(event.event_date)
    switch (filter) {
      case 'thisweek':
        return isThisWeek(eventDate, { weekStartsOn: 1 })
      case 'thismonth':
        const now = new Date()
        return eventDate.getMonth() === now.getMonth() &&
               eventDate.getFullYear() === now.getFullYear()
      default:
        return true
    }
  })

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="space-y-2">
          <div className="h-12 bg-gray-100 rounded"></div>
          <div className="h-12 bg-gray-100 rounded"></div>
        </div>
      </div>
    )
  }

  if (compact) {
    // Compact view for dashboard
    const upcomingEvents = filteredEvents.slice(0, 3)
    
    if (upcomingEvents.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
            <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
            School Events
          </h3>
          <p className="text-sm text-gray-500">No upcoming events</p>
        </div>
      )
    }

    return (
      <div className="bg-white rounded-lg shadow-sm p-4">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
          <GraduationCap className="w-5 h-5 mr-2 text-blue-600" />
          School Events
        </h3>
        <div className="space-y-2">
          {upcomingEvents.map(event => {
            const daysFromToday = getDaysFromToday(event.event_date)
            const isUrgent = daysFromToday >= 0 && daysFromToday <= 2
            
            return (
              <div 
                key={event.id} 
                className={`flex items-start space-x-3 p-2 rounded-lg ${
                  isUrgent ? 'bg-yellow-50' : 'bg-gray-50'
                }`}
              >
                <div className={`p-1.5 rounded ${getEventColor(event.event_type)}`}>
                  {getEventIcon(event.event_type)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="font-medium text-sm text-gray-900 truncate">
                        {event.event_name}
                      </p>
                      {/* Filter out UUID school names */}
                      {!isUUID(event.school_name) && (
                        <p className="text-xs text-gray-500">
                          {event.school_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-2">
                      <p className={`text-xs font-medium ${
                        isUrgent ? 'text-orange-600' : 'text-gray-600'
                      }`}>
                        {formatEventDate(event.event_date)}
                      </p>
                      {event.event_time && !event.all_day && (
                        <p className="text-xs text-gray-500">
                          {formatEventTimeDisplay(event)}
                        </p>
                      )}
                    </div>
                  </div>
                  {event.is_mandatory && (
                    <span className="inline-flex items-center mt-1">
                      <AlertCircle className="w-3 h-3 text-red-500 mr-1" />
                      <span className="text-xs text-red-600">Required</span>
                    </span>
                  )}
                </div>
              </div>
            )
          })}
        </div>
        {events.length > 3 && (
          <p className="text-xs text-gray-500 mt-3 text-center">
            +{events.length - 3} more events
          </p>
        )}
      </div>
    )
  }

  // Full view
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-gray-900 flex items-center">
          <GraduationCap className="w-6 h-6 mr-2 text-blue-600" />
          School Calendar
        </h2>
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          {(['thisweek', 'thismonth', 'all'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1 rounded text-sm font-medium capitalize transition-colors ${
                filter === f 
                  ? 'bg-white text-blue-600 shadow-sm' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {f === 'thisweek' ? 'This Week' : f === 'thismonth' ? 'This Month' : 'All'}
            </button>
          ))}
        </div>
      </div>

      {filteredEvents.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-8 text-center">
          <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No upcoming events
          </h3>
          <p className="text-gray-600">
            Check back later for new school events and activities.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {filteredEvents.map(event => {
            const eventDate = parseLocalDate(event.event_date)
            const daysUntil = differenceInDays(eventDate, new Date())
            const isUrgent = daysUntil >= 0 && daysUntil <= 2
            
            return (
              <div 
                key={event.id}
                className={`bg-white rounded-lg shadow-sm border-2 p-4 ${
                  isUrgent ? 'border-orange-200 bg-orange-50' : 'border-gray-200'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <div className={`p-2 rounded-lg ${getEventColor(event.event_type)}`}>
                      {getEventIcon(event.event_type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">
                        {event.event_name}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {event.school_name}
                      </p>
                    </div>
                  </div>
                  {event.is_mandatory && (
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs font-medium">
                      Required
                    </span>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-4 h-4 mr-2" />
                    <span className={isUrgent ? 'font-medium text-orange-600' : ''}>
                      {format(eventDate, 'EEEE, MMMM d, yyyy')}
                    </span>
                  </div>
                  
                  {event.event_time && !event.all_day && (
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-2" />
                      <span>{formatEventTime(event)}</span>
                    </div>
                  )}
                  
                  {event.location && (
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      <span>{event.location}</span>
                    </div>
                  )}
                  
                  {event.description && (
                    <p className="text-gray-700 mt-2 pt-2 border-t">
                      {event.description}
                    </p>
                  )}
                </div>

                {isUrgent && daysUntil >= 0 && (
                  <div className="mt-3 flex items-center text-orange-600 text-sm font-medium">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    {daysUntil === 0 ? 'Today!' : 
                     daysUntil === 1 ? 'Tomorrow!' : 
                     `In ${daysUntil} days`}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}